<template>
  <div class="items-panel">
    <div class="items-panel__toolbar">
      <div class="items-panel__toolbar-text">
        <div class="ds-section-header">Tickets in this release</div>
        <div class="items-panel__hint">
          Categories and customer titles are saved as you edit them. They shape the next generated
          draft — they do not rewrite the draft you already have.
        </div>
      </div>

      <v-spacer />

      <v-tooltip v-if="canEdit" bottom :disabled="canResyncItems">
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">
            <v-btn small depressed color="primary" :disabled="!canResyncItems" @click="openPicker">
              <v-icon left small>mdi-playlist-edit</v-icon>
              Edit selected tickets
            </v-btn>
          </span>
        </template>
        <span>
          This release is not linked to a Jira version, so its tickets can’t be re-synced from Jira.
        </span>
      </v-tooltip>
    </div>

    <v-divider />

    <v-data-table
      class="elevation-0 pa-2 rounded-0 custom-table"
      :headers="headers"
      :items="rows"
      :loading="isItemsLoading"
      item-key="id"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
        itemsPerPageText: 'Row/Page',
        itemsPerPageOptions: [15, 30, 50, 100, -1],
      }">
      <template v-slot:[`item.key`]="{ item }">
        <v-chip
          v-if="item.key && item.browseUrl"
          small
          outlined
          color="primary"
          class="items-panel__key-chip"
          :href="item.browseUrl"
          target="_blank"
          rel="noopener">
          {{ item.key }}
          <v-icon x-small right>mdi-open-in-new</v-icon>
        </v-chip>
        <v-chip v-else-if="item.key" small outlined class="items-panel__key-chip">
          {{ item.key }}
        </v-chip>
        <span v-else>—</span>
      </template>

      <template v-slot:[`item.summary`]="{ item }">
        <span class="items-panel__summary">{{ item.summary || '—' }}</span>
      </template>

      <template v-slot:[`item.type`]="{ item }">
        <div v-if="item.type" class="items-panel__type">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small :color="item.typeIconColor" v-bind="attrs" v-on="on">
                {{ item.typeIcon }}
              </v-icon>
            </template>
            <span>{{ item.type }}</span>
          </v-tooltip>
          <span class="items-panel__type-text">{{ item.type }}</span>
        </div>
        <span v-else>—</span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <span>{{ item.status || '—' }}</span>
      </template>

      <template v-slot:[`item.category`]="{ item }">
        <v-select
          v-if="canEdit"
          outlined
          dense
          hide-details
          class="items-panel__input"
          :items="categoryOptions"
          :value="item.category"
          :disabled="isRowSaving(item.id)"
          :error="isRowFailed(item.id)"
          @change="onCategoryChange(item, $event)">
          <template v-slot:selection="{ item: option }">
            <span class="items-panel__category">
              <v-icon small :color="option.color">{{ option.icon }}</v-icon>
              <span class="items-panel__category-text">{{ option.text }}</span>
            </span>
          </template>
          <template v-slot:item="{ item: option, on, attrs }">
            <v-list-item v-bind="attrs" dense v-on="on">
              <v-list-item-icon class="mr-2">
                <v-icon small :color="option.color">{{ option.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ option.text }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-select>
        <span v-else class="items-panel__category">
          <v-icon small :color="categoryMeta(item.category).color">
            {{ categoryMeta(item.category).icon }}
          </v-icon>
          <span class="items-panel__category-text">{{ categoryLabel(item.category) }}</span>
        </span>
      </template>

      <template v-slot:[`item.customerTitle`]="{ item }">
        <v-text-field
          v-if="canEdit"
          outlined
          dense
          hide-details
          class="items-panel__input"
          :placeholder="item.summary"
          :value="item.customerTitle"
          :disabled="isRowSaving(item.id)"
          :error="isRowFailed(item.id)"
          @input="onTitleInput(item, $event)"
          @blur="onTitleBlur(item)" />
        <span v-else>{{ item.customerTitle || item.summary || '—' }}</span>
      </template>

      <template v-slot:[`item.state`]="{ item }">
        <v-progress-circular v-if="isRowSaving(item.id)" indeterminate size="16" width="2" />
        <v-tooltip v-else-if="isRowFailed(item.id)" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small color="error" v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon>
          </template>
          <span>The last change could not be saved and was rolled back.</span>
        </v-tooltip>
      </template>

      <template v-slot:no-data>
        <div class="ds-empty-state">
          <v-icon class="ds-empty-state__icon">mdi-ticket-outline</v-icon>
          <div class="ds-empty-state__title">No tickets yet</div>
          <div class="ds-empty-state__description">
            This release has no tickets attached. Re-sync from Jira to pick the tickets it should
            cover.
          </div>
        </div>
      </template>

      <template v-slot:loading>
        <div class="text-center d-flex flex-column">
          <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
          <p class="font-weight-medium">Loading tickets...</p>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="pickerOpen" persistent max-width="1100" scrollable>
      <v-card class="ds-dialog" color="bg1">
        <div class="ds-dialog__header">
          <div class="ds-dialog__header-icon">
            <v-icon color="primary">mdi-playlist-edit</v-icon>
          </div>
          <div class="ds-dialog__header-text">
            <div class="ds-dialog__title">Edit selected tickets</div>
            <div class="ds-dialog__subtitle">Choose which Jira tickets this release covers.</div>
          </div>
          <v-btn icon :disabled="savingItems" @click="closePicker">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="ds-dialog__body">
          <div class="ds-info-box mb-4">
            <v-icon small class="mr-2">mdi-information-outline</v-icon>
            <span>
              Saving this re-syncs the ticket list from Jira. It does <strong>not</strong> change
              the release notes you already have — the current draft is left exactly as it is. To
              fold the new ticket list into the notes, regenerate afterwards.
            </span>
            <v-btn
              small
              text
              color="primary"
              class="mt-2"
              :disabled="savingItems"
              @click="onRegenerateShortcut">
              <v-icon left small>mdi-auto-fix</v-icon>
              Regenerate from items
            </v-btn>
          </div>

          <issue-selection-table
            :issues="jiraIssues"
            :loading="isJiraIssuesLoading"
            :value="selectedKeys"
            @input="selectedKeys = $event" />
        </div>

        <div class="ds-dialog__footer">
          <span class="ds-dialog__hint">
            {{ selectedKeys.length }} ticket{{ selectedKeys.length === 1 ? '' : 's' }} selected
          </span>
          <v-spacer />
          <v-btn text small :disabled="savingItems" @click="closePicker">Cancel</v-btn>
          <v-btn
            small
            depressed
            color="primary"
            :loading="savingItems"
            :disabled="isJiraIssuesLoading"
            @click="confirmPicker">
            Save tickets
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import permissions from '@Share/config/permissions';
  import { $has } from '@Utils';
  import { error } from '@/utils/debugLogger';
  import {
    ReleaseItemCategory,
    ReleaseItemCategoryLabels,
    releaseItemCategoryMeta,
  } from '@ReleaseNotes/config/enums';
  import { issueTypeMeta, readJiraUrl } from '@ReleaseNotes/config/jiraMeta';
  import IssueSelectionTable from '@ReleaseNotes/components/IssueSelectionTable.vue';

  const TITLE_DEBOUNCE_MS = 600;

  /** Display order for the category picker — "Other" last, everything else by usefulness. */
  const CATEGORY_ORDER = [
    ReleaseItemCategory.Feature,
    ReleaseItemCategory.Improvement,
    ReleaseItemCategory.Fix,
    ReleaseItemCategory.Security,
    ReleaseItemCategory.Breaking,
    ReleaseItemCategory.Other,
  ];

  /**
   * Reads the first present value among several candidate property names.
   * The ReleaseItemDto shape is not pinned down client-side, so every field is probed defensively.
   * @param {Object} source object to read from
   * @param {string[]} keys candidate property names, most-likely first
   * @param {*} [fallback]
   * @returns {*}
   */
  function pick(source, keys, fallback = null) {
    if (!source) return fallback;
    const key = keys.find(
      (candidate) => source[candidate] !== undefined && source[candidate] !== null,
    );
    return key ? source[key] : fallback;
  }

  export default {
    name: 'ItemsPanel',

    components: { IssueSelectionTable },

    props: {
      release: { type: Object, default: null },
      releaseId: { type: [String, Number], default: null },
    },

    data() {
      return {
        permissions,
        headers: [
          { text: 'Key', value: 'key', width: 120, sortable: true },
          { text: 'Summary', value: 'summary', sortable: true },
          { text: 'Type', value: 'type', width: 120, sortable: true },
          { text: 'Status', value: 'status', width: 130, sortable: true },
          { text: 'Category', value: 'category', width: 190, sortable: true },
          { text: 'Customer title', value: 'customerTitle', width: 280, sortable: false },
          { text: '', value: 'state', width: 40, sortable: false },
        ],
        // itemId -> { category, customerTitle } — local truth while a PATCH is in flight or settled.
        overrides: {},
        // itemId -> { saving: Boolean, failed: Boolean }
        rowState: {},
        pickerOpen: false,
        selectedKeys: [],
        savingItems: false,
      };
    },

    computed: {
      canEdit() {
        return $has(permissions.ReleaseNotes.Edit);
      },

      isItemsLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_CURRENT_RELEASE_LOADING'];
      },

      jiraIssues() {
        return this.$store.getters['RELEASE_NOTES/GET_JIRA_ISSUES'] || [];
      },

      isJiraIssuesLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_JIRA_ISSUES_LOADING'];
      },

      categoryOptions() {
        return CATEGORY_ORDER.map((value) => {
          const meta = releaseItemCategoryMeta(value);
          return {
            value,
            text: ReleaseItemCategoryLabels[value],
            icon: meta.icon,
            color: meta.color,
          };
        });
      },

      /**
       * The Jira version this release was generated from. Manually-created releases have none,
       * which is why the re-sync affordance is disabled rather than firing with an undefined id.
       */
      jiraVersionId() {
        const release = this.release;
        if (!release) return null;
        const nested = release.jiraVersion || null;
        return pick(release, ['jiraVersionId', 'versionId']) ?? pick(nested, ['id', 'versionId']);
      },

      canResyncItems() {
        return !!this.releaseId && !!this.jiraVersionId;
      },

      sourceItems() {
        return (this.release && this.release.items) || [];
      },

      /** Normalised rows — server values overlaid with any local edit that has been applied. */
      rows() {
        return this.sourceItems.map((item) => {
          const id = pick(item, ['id', 'itemId', 'releaseItemId']);
          const override = this.overrides[id] || {};
          const serverCategory = pick(item, ['category'], ReleaseItemCategory.Other);
          const serverTitle = pick(item, ['customerTitle'], '') || '';
          const type = pick(item, ['issueType', 'type', 'jiraIssueType'], '');
          const typeMeta = issueTypeMeta(type);
          const key = pick(item, ['jiraKey', 'issueKey', 'key'], '');
          return {
            id,
            key,
            browseUrl: readJiraUrl(item, key),
            summary: pick(item, ['summary', 'jiraSummary', 'title'], ''),
            type,
            typeIcon: typeMeta.icon,
            typeIconColor: typeMeta.color,
            status: pick(item, ['status', 'jiraStatus'], ''),
            category: override.category !== undefined ? override.category : serverCategory,
            customerTitle:
              override.customerTitle !== undefined ? override.customerTitle : serverTitle,
          };
        });
      },

      currentIssueKeys() {
        return this.rows.map((row) => row.key).filter((key) => !!key);
      },
    },

    watch: {
      // A fresh detail fetch is authoritative: drop local overlays for items whose server value
      // already matches, so the panel never keeps showing a stale "local" value indefinitely.
      sourceItems() {
        this.reconcileOverrides();
      },
    },

    created() {
      // Non-reactive timer bag: itemId -> setTimeout handle for the debounced customer-title save.
      this.titleTimers = {};
      // Non-reactive sequence bag: itemId -> monotonically increasing edit number, used to drop
      // responses belonging to a superseded edit.
      this.editSeq = {};
      // Non-reactive bag of values this component has successfully persisted since the last
      // detail fetch: itemId -> { category?, customerTitle? }. `release.items` goes stale the
      // moment an inline PATCH succeeds (no re-fetch per keystroke), so this — not the prop — is
      // the reference for "has this actually changed?" and for rolling back a failed save.
      this.savedValues = {};
    },

    beforeDestroy() {
      this.clearAllTimers();
    },

    methods: {
      $has,

      /** Clears every outstanding debounce timer. Called from beforeDestroy and before a re-sync. */
      clearAllTimers() {
        Object.keys(this.titleTimers).forEach((id) => {
          clearTimeout(this.titleTimers[id]);
        });
        this.titleTimers = {};
      },

      categoryMeta: releaseItemCategoryMeta,

      categoryLabel(category) {
        const label = ReleaseItemCategoryLabels[category];
        return label || ReleaseItemCategoryLabels[ReleaseItemCategory.Other];
      },

      isRowSaving(id) {
        const state = this.rowState[id];
        return !!(state && state.saving);
      },

      isRowFailed(id) {
        const state = this.rowState[id];
        return !!(state && state.failed);
      },

      setRowState(id, patch) {
        const current = this.rowState[id] || { saving: false, failed: false };
        this.$set(this.rowState, id, { ...current, ...patch });
      },

      setOverride(id, patch) {
        const current = this.overrides[id] || {};
        this.$set(this.overrides, id, { ...current, ...patch });
      },

      clearOverrideField(id, field) {
        const current = this.overrides[id];
        if (!current || current[field] === undefined) return;
        const next = { ...current };
        delete next[field];
        if (Object.keys(next).length === 0) this.$delete(this.overrides, id);
        else this.$set(this.overrides, id, next);
      },

      /** Finds the server-side item behind a normalised row id. */
      findSourceItem(id) {
        return this.sourceItems.find(
          (candidate) => String(pick(candidate, ['id', 'itemId', 'releaseItemId'])) === String(id),
        );
      },

      /**
       * The value currently believed to be persisted for one field of one item: whatever this
       * component last saved successfully, falling back to the value the detail fetch delivered.
       * @param {*} id item id
       * @param {string} field 'category' | 'customerTitle'
       * @returns {*}
       */
      persistedValue(id, field) {
        const saved = this.savedValues[id];
        if (saved && saved[field] !== undefined) return saved[field];
        const item = this.findSourceItem(id);
        if (field === 'category') return pick(item, ['category'], ReleaseItemCategory.Other);
        return pick(item, ['customerTitle'], '') || '';
      },

      rememberSaved(id, field, value) {
        this.savedValues[id] = { ...(this.savedValues[id] || {}), [field]: value };
      },

      /** Drops overlays that the server has caught up with, and overlays for items that vanished. */
      reconcileOverrides() {
        Object.keys(this.overrides).forEach((id) => {
          const item = this.findSourceItem(id);
          if (!item) {
            this.$delete(this.overrides, id);
            this.$delete(this.rowState, id);
            delete this.savedValues[id];
            return;
          }
          const override = this.overrides[id];
          const serverCategory = pick(item, ['category'], ReleaseItemCategory.Other);
          const serverTitle = pick(item, ['customerTitle'], '') || '';
          if (override.category !== undefined && override.category === serverCategory) {
            this.clearOverrideField(id, 'category');
            if (this.savedValues[id]) delete this.savedValues[id].category;
          }
          if (override.customerTitle !== undefined && override.customerTitle === serverTitle) {
            this.clearOverrideField(id, 'customerTitle');
            if (this.savedValues[id]) delete this.savedValues[id].customerTitle;
          }
        });
      },

      nextSeq(id) {
        this.editSeq[id] = (this.editSeq[id] || 0) + 1;
        return this.editSeq[id];
      },

      /**
       * PATCHes one field of one item, keeping the local overlay as the displayed truth.
       * A response is only acted on when its sequence number is still the newest for that item,
       * so a slow response can never clobber a newer local edit.
       * @param {*} id item id
       * @param {string} field 'category' | 'customerTitle'
       * @param {*} value new value
       * @param {*} previous value to roll back to when the request fails
       */
      saveField(id, field, value, previous) {
        if (id === null || id === undefined || !this.releaseId) return;
        const seq = this.nextSeq(id);

        this.setOverride(id, { [field]: value });
        this.setRowState(id, { saving: true, failed: false });

        this.$store
          .dispatch('RELEASE_NOTES/UPDATE_ITEM', {
            id: this.releaseId,
            itemId: id,
            data: { [field]: value },
          })
          .then(() => {
            this.rememberSaved(id, field, value);
            if (this.editSeq[id] !== seq) return;
            this.setRowState(id, { saving: false, failed: false });
          })
          .catch((err) => {
            // The Vuex action already surfaced the error through $handleError; all that is left
            // is to stop the UI claiming a value was persisted when it was not.
            error('ItemsPanel: failed to save release item field', err);
            if (this.editSeq[id] !== seq) return;
            this.setOverride(id, { [field]: previous });
            this.setRowState(id, { saving: false, failed: true });
          });
      },

      onCategoryChange(row, value) {
        const previous = this.persistedValue(row.id, 'category');
        if (value === previous) {
          this.setOverride(row.id, { category: value });
          return;
        }
        this.saveField(row.id, 'category', value, previous);
      },

      onTitleInput(row, value) {
        const id = row.id;
        const next = value || '';
        const previous = this.persistedValue(id, 'customerTitle');
        // Keep the field responsive without dispatching on every keystroke.
        this.setOverride(id, { customerTitle: next });
        if (this.titleTimers[id]) clearTimeout(this.titleTimers[id]);
        if (next === previous) {
          delete this.titleTimers[id];
          return;
        }
        this.titleTimers[id] = setTimeout(() => {
          delete this.titleTimers[id];
          this.saveField(id, 'customerTitle', next, previous);
        }, TITLE_DEBOUNCE_MS);
      },

      onTitleBlur(row) {
        const id = row.id;
        // A pending debounce means there is an unsaved change — flush it immediately.
        if (!this.titleTimers[id]) return;
        clearTimeout(this.titleTimers[id]);
        delete this.titleTimers[id];
        const override = this.overrides[id] || {};
        const next = override.customerTitle !== undefined ? override.customerTitle : '';
        const previous = this.persistedValue(id, 'customerTitle');
        if (next === previous) return;
        this.saveField(id, 'customerTitle', next, previous);
      },

      openPicker() {
        if (!this.canResyncItems) return;
        this.selectedKeys = [...this.currentIssueKeys];
        this.pickerOpen = true;
        this.$store.dispatch('RELEASE_NOTES/FETCH_JIRA_VERSION_ISSUES', this.jiraVersionId);
      },

      closePicker() {
        if (this.savingItems) return;
        this.pickerOpen = false;
      },

      onRegenerateShortcut() {
        this.pickerOpen = false;
        this.$emit('regenerate');
      },

      confirmPicker() {
        if (this.savingItems) return;
        this.savingItems = true;
        // A whole-list re-sync replaces every item id, so any in-flight per-item debounce is moot.
        this.clearAllTimers();
        this.$store
          .dispatch('RELEASE_NOTES/UPDATE_ITEMS', {
            id: this.releaseId,
            data: { issueKeys: this.selectedKeys },
          })
          .then(() => {
            this.overrides = {};
            this.rowState = {};
            this.editSeq = {};
            this.savedValues = {};
            this.pickerOpen = false;
            this.$dialog.notify.success('Tickets updated from Jira.', {
              position: 'top-right',
              timeout: 3000,
            });
            this.$emit('refresh');
          })
          .catch((err) => {
            error('ItemsPanel: failed to re-sync release items', err);
          })
          .finally(() => {
            this.savingItems = false;
          });
      },
    },
  };
</script>

<style scoped lang="scss">
  .items-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .items-panel__toolbar {
    display: flex;
    align-items: flex-start;
    gap: var(--ds-space-md, 16px);
    padding: var(--ds-space-md, 16px);
  }

  .items-panel__toolbar-text {
    min-width: 0;
  }

  .items-panel__hint {
    font-size: 0.75rem;
    color: var(--v-text_secondary-base);
    max-width: 620px;
    margin-top: 2px;
  }

  .items-panel__summary {
    display: inline-block;
    max-width: 420px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  .items-panel__input {
    font-size: 0.8125rem;
  }

  .items-panel__key-chip {
    font-weight: 500;
    font-size: 0.75rem;
  }

  a.items-panel__key-chip {
    text-decoration: none;
  }

  .items-panel__type {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .items-panel__type-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .items-panel__category {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .items-panel__category-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .items-panel__input .v-input__slot {
    min-height: 32px !important;
  }
</style>
